import React, { useEffect, useState, useRef } from "react";
import { HashRouter, Route, Switch, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { getUserInfo } from "@/store/actions";
import Layout from "@/views/layout";
import Login from "@/views/login";
import Loading from "@/components/Loading";
import { clearLocalDatas } from "@/utils/auth";
import tokenManager, { isTokenExpired } from "@/utils/tokenManager";

const Router = ({ token, role, getUserInfo }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  
  // 使用ref跟踪组件是否已卸载
  const isMountedRef = useRef(true);

  // 权限检查逻辑
  useEffect(() => {
    const checkAuth = async () => {
      
      // 如果已经检查过权限，且没有token，不重复检查
      if (authChecked && !token) {
        tokenManager.stopTokenMonitoring(); // 停止token监控
        return;
      }
      
      // 安全的状态更新函数
      const safeSetState = (updateFn) => {
        if (isMountedRef.current) {
          updateFn();
        }
      };
      
      safeSetState(() => {
        setIsLoading(true);
        setAuthFailed(false);
      });
      
      // 第一步：检查是否有token
      if (!token) {
        safeSetState(() => {
          setAuthChecked(true);
          setAuthFailed(true);
          setIsLoading(false);
        });
        tokenManager.stopTokenMonitoring(); // 停止token监控
        return;
      }

      // 第二步：检查token是否过期
      if (isTokenExpired(token)) {
        clearLocalDatas(["token"]);
        safeSetState(() => {
          setAuthChecked(true);
          setAuthFailed(true);
          setIsLoading(false);
        });
        tokenManager.stopTokenMonitoring(); // 停止token监控
        return;
      }

      // 第三步：如果已有有效的role，说明用户信息已获取
      if (role && role.trim() !== '') {
        safeSetState(() => {
          setAuthChecked(true);
          setAuthFailed(false);
          setIsLoading(false);
        });
        
        // 启动token监控
        tokenManager.startTokenMonitoring();
        return;
      }

      // 第四步：有token但没有有效role，校验token并获取用户信息
      try {
        const response = await getUserInfo(token);
        safeSetState(() => {
          setAuthChecked(true);
          setAuthFailed(false);
          setIsLoading(false);
        });
        
        // 启动token监控
        tokenManager.startTokenMonitoring();
      } catch (error) {
        clearLocalDatas(["token"]);
        safeSetState(() => {
          setAuthChecked(true);
          setAuthFailed(true);
          setIsLoading(false);
        });
        tokenManager.stopTokenMonitoring(); // 停止token监控
      }
    };

    checkAuth();
  }, [token, role, getUserInfo]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      tokenManager.stopTokenMonitoring();
    };
  }, []);

  // 权限检查中，显示加载页面
  if (isLoading || (!authChecked && token)) {
    return <Loading />;
  }


  return (
    <HashRouter>
      <Switch>
        <Route exact path="/login" component={Login} />
        <Route
          path="/"
          render={() => {
            // 权限检查：没有token或认证失败或没有有效用户角色
            if (!token || authFailed || !role || role.trim() === '') {
              return <Redirect to="/login" />;
            }
            
            // 通过权限检查，显示主页面
            return <Layout />;
          }}
        />
      </Switch>
    </HashRouter>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.user.token,
    role: state.user.role
  };
};

export default connect(mapStateToProps, { getUserInfo })(Router);
